import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ChatRoom } from "@/hooks/use-chat-rooms";
import { PresenceDot } from "@/components/shared/PresenceDot";
import { SrIcon } from "@/components/ui/SrIcon";
import { ActiveNowStrip } from "./ActiveNowStrip";
import { useChatActions } from "@/hooks/use-chat-actions";
import { matchesSearch, roomTitle } from "./room-search";

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - then.getTime()) / 60000);
  if (diffMin < 1) return "nå";
  if (diffMin < 60) return `${diffMin}m`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}t`;
  return then.toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit" });
}

function RoomAvatar({ room, currentUserId }: { room: ChatRoom; currentUserId: string }) {
  if (room.type !== "direct") {
    return (
      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center shrink-0">
        <SrIcon name="users" className="w-4 h-4"  />
      </div>
    );
  }
  const other = room.otherUser ?? room.members.find((m) => m.user_id !== currentUserId) ?? null;
  const name = other?.full_name ?? "?";
  const initials = name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="relative shrink-0">
      {other?.avatar_url ? (
        <img src={other.avatar_url} alt={name} className="w-9 h-9 rounded-full object-cover" />
      ) : (
        <span className="w-9 h-9 rounded-full bg-primary/10 text-primary text-xs font-medium inline-flex items-center justify-center">
          {initials}
        </span>
      )}
      <PresenceDot
        userId={other?.user_id}
        size="md"
        className="absolute bottom-0 right-0"
      />
    </div>
  );
}


export function ChatRoomList({
  rooms,
  isLoading,
  currentUserId,
  onSelect,
  onNewChat,
}: {
  rooms: ChatRoom[];
  isLoading: boolean;
  currentUserId: string;
  onSelect: (room: ChatRoom) => void;
  onNewChat: () => void;
}) {
  const [search, setSearch] = useState("");
  const { createDirectChat } = useChatActions(currentUserId);

  const filtered = rooms.filter((r) => matchesSearch(r, search, currentUserId));

  // VIB-728 A: Klikk på avatar i ActiveNowStrip → åpne eksisterende DM eller
  // opprett ny. createDirectChat-mutation håndterer begge cases (find-or-create).
  const handleSelectActiveUser = async (userId: string) => {
    const existingDM = rooms.find(
      (r) => r.type === "direct" && r.members.some((m) => m.user_id === userId),
    );
    if (existingDM) {
      onSelect(existingDM);
      return;
    }
    try {
      const roomId = await createDirectChat.mutateAsync(userId);
      const newRoom = rooms.find((r) => r.id === roomId);
      if (newRoom) onSelect(newRoom);
      // Hvis ikke i lista enda (realtime henter den straks), faller vi
      // bare tilbake til at brukeren ser den i listen og kan klikke selv.
    } catch (e) {
      console.error("Kunne ikke åpne DM:", e);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-card shrink-0">
        <SrIcon name="message-square" className="w-4 h-4 text-primary"  />
        <span className="font-medium text-sm flex-1">Chat</span>
        <Button size="sm" onClick={onNewChat} className="h-7 text-xs gap-1">
          <SrIcon name="plus" className="w-3.5 h-3.5"  /> Ny
        </Button>
      </div>

      <ActiveNowStrip
        currentUserId={currentUserId}
        onSelectUser={handleSelectActiveUser}
      />

      <div className="px-3 py-2 border-b shrink-0">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søk i samtaler..."
          className="w-full h-8 rounded-md border bg-background px-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex justify-center py-6">
            <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground"  />
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <p className="text-center text-xs text-muted-foreground py-8 px-4">
            {search
              ? "Ingen samtaler matcher søket."
              : "Ingen samtaler ennå. Start en ny for å komme i gang."}
          </p>
        )}
        {filtered.map((room) => (
          <button
            key={room.id}
            type="button"
            onClick={() => onSelect(room)}
            className={cn(
              "w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors border-b border-border/40",
              room.unreadCount > 0 && "bg-primary/5",
            )}
          >
            <RoomAvatar room={room} currentUserId={currentUserId} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className={cn("text-sm truncate", room.unreadCount > 0 ? "font-semibold" : "font-medium")}>
                  {roomTitle(room, currentUserId)}
                </span>
                <span className="ml-auto text-[10px] text-muted-foreground shrink-0">
                  {formatRelativeTime(room.last_message_at)}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={cn("text-xs truncate flex-1 min-w-0", room.unreadCount > 0 ? "text-foreground" : "text-muted-foreground")}>
                  {room.lastMessagePreview ?? "—"}
                </span>
                {room.unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-1.5 shrink-0">
                    {room.unreadCount}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default ChatRoomList;
